import * as React from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { baseURL } from "services/API";
import {
  Box,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useBrokers, useAttachLandlord, useLandlords } from "services/hooks/rentals";
import FormDialog from "components/modal";
import BrokersTable from "components/Tables/BrokersTable";
import BrokerForm from "components/Forms/BrokersForm";
import { BrokerLandlordsForm } from "components/Forms/BrokerLandlordForm";
import { useState } from "react";

const emptyBroker = () => ({
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  broker_name: "",
  broker_location: "",
  broker_nin: "",
});

export default function Brokers() {
  const token = useSelector((state) => state.token);
  const role = useSelector((state) => state.role);
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningBroker, setAssigningBroker] = useState(null);
  const [assignLandlordId, setAssignLandlordId] = useState("");
  const [assignCommissionRate, setAssignCommissionRate] = useState("");
  const [assignCommissionBase, setAssignCommissionBase] = useState("gross");
  const [errors, setErrors] = useState({});
  const [isSubmittingData, setIsSubmittingData] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [newBroker, setNewBroker] = useState(emptyBroker());

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const { brokers, isLoadingBrokers, error, refetch } = useBrokers();
  const { landlords, isLoadingLandlords } = useLandlords({ inactivation: "active" });
  const { attachLandlord, loading: attachingLandlord } = useAttachLandlord();

  const isManager = role === "manager" || role === "admin";

  const handleMenuClick = (event, broker) => {
    setAnchorEl(event.currentTarget);
    setSelectedBroker(broker);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAssign = (broker) => {
    setAssigningBroker(broker);
    setAssignLandlordId("");
    setAssignCommissionRate("");
    setAssignCommissionBase("gross");
    setAssignOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setOpen(false);
    setNewBroker(emptyBroker());
    setErrors({});
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewBroker((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!newBroker.username?.trim()) nextErrors.username = "Username is required.";
    if (!newBroker.password) nextErrors.password = "Password is required.";
    if (!newBroker.first_name?.trim()) nextErrors.first_name = "First name is required.";
    if (!newBroker.last_name?.trim()) nextErrors.last_name = "Last name is required.";
    if (!newBroker.broker_name?.trim()) nextErrors.broker_name = "Broker name is required.";
    if (!newBroker.broker_location?.trim()) nextErrors.broker_location = "Location is required.";
    if (!newBroker.phone_number?.trim()) {
      nextErrors.phone_number = "Phone number is required.";
    } else if (!/^(0[0-9]{9}|(\+\d{1,3}[0-9]{9}))$/.test(newBroker.phone_number.trim())) {
      nextErrors.phone_number = "Valid format: 0751234567 or +256777222333.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitBroker = async () => {
    if (!validateForm()) {
      setMessage({ type: "error", text: "Please fix the highlighted fields." });
      return;
    }

    setIsSubmittingData(true);
    setMessage({ type: "", text: "" });
    try {
      const phone = newBroker.phone_number.trim();
      const payload = {
        username: newBroker.username.trim(),
        password: newBroker.password,
        first_name: newBroker.first_name.trim(),
        last_name: newBroker.last_name.trim(),
        email: newBroker.email?.trim() || undefined,
        phone_number: phone,
        broker_name: newBroker.broker_name.trim(),
        broker_location: newBroker.broker_location.trim(),
        broker_contact: phone,
        broker_nin: newBroker.broker_nin?.trim() || null,
      };
      await axios.post(`${baseURL}/landlords/admin/brokers`, payload);
      setMessage({ type: "success", text: "Broker created with login account." });
      refetch();
      handleDialogClose();
    } catch (err) {
      const errBody = err.response?.data;
      setMessage({
        type: "error",
        text: errBody?.error || errBody?.message || "Failed to create broker.",
      });
    } finally {
      setIsSubmittingData(false);
    }
  };

  const selectedLandlord = landlords.find((l) => l.id === Number(assignLandlordId));
  const selectedLandlordMaxRate = selectedLandlord?.pay_percentage ?? selectedLandlord?.max_pay_percentage;

  const handleAssignLandlord = async () => {
    if (!assigningBroker?.id || !assignLandlordId) {
      setMessage({ type: "error", text: "Select a landlord." });
      return;
    }
    if (!assignCommissionRate && assignCommissionRate !== 0) {
      setMessage({ type: "error", text: "Commission rate is required." });
      return;
    }
    const rate = Number(assignCommissionRate);
    if (Number.isNaN(rate) || rate < 0) {
      setMessage({ type: "error", text: "Enter a valid commission rate." });
      return;
    }
    if (assignCommissionBase === "net" && rate > 100) {
      setMessage({ type: "error", text: "Net commission rate cannot exceed 100%." });
      return;
    }
    if (assignCommissionBase !== "net" && selectedLandlordMaxRate != null && rate > Number(selectedLandlordMaxRate)) {
      setMessage({
        type: "error",
        text: `Gross commission rate cannot exceed landlord pay percentage (${selectedLandlordMaxRate}%).`,
      });
      return;
    }

    const response = await attachLandlord(
      Number(assigningBroker.id),
      Number(assignLandlordId),
      rate,
      assignCommissionBase
    );
    if (response.success) {
      setMessage({ type: "success", text: "Landlord assigned to broker." });
      setAssignOpen(false);
      setAssigningBroker(null);
      refetch();
    } else {
      setMessage({ type: "error", text: response.message });
    }
  };

  if (!isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Admin or Property Manager can manage brokers.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography fontWeight="bold" fontSize="25px">
          Brokers
        </Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Add Broker
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create brokers with login credentials, then assign landlords with a commission rate (%) and base (gross/net).
        Brokers log in to view commissions and payment status.
      </Typography>

      {message.text && (
        <Alert
          severity={message.type === "error" ? "error" : "success"}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <FormDialog
        open={open}
        title="Add Broker"
        text="Create broker profile and login account"
        content={
          <BrokerForm
            open={open}
            handleDialogClose={handleDialogClose}
            handleChange={handleChange}
            newBroker={newBroker}
            errors={errors}
          />
        }
        handleSubmit={handleSubmitBroker}
        handleClose={handleDialogClose}
        isSubmittingData={isSubmittingData}
      />

      <BrokersTable
        brokers={brokers}
        isLoadingBrokers={isLoadingBrokers}
        error={error}
        newSelectedBroker={selectedBroker}
        handleMenuClick={handleMenuClick}
        handleMenuClose={handleMenuClose}
        onAssignLandlord={handleOpenAssign}
        anchorEl={anchorEl}
      />

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign landlord — {assigningBroker?.broker_name || "Broker"}
        </DialogTitle>
        <DialogContent>
          <BrokerLandlordsForm
            newLandlord={assignLandlordId}
            brokerCommissionRate={assignCommissionRate}
            commissionBase={assignCommissionBase}
            handleChange={(e) => setAssignLandlordId(e.target.value)}
            handleCommissionRateChange={(e) => setAssignCommissionRate(e.target.value)}
            handleCommissionBaseChange={(e) => setAssignCommissionBase(e.target.value)}
            landlords={landlords}
            isLoadingLandlords={isLoadingLandlords}
            selectedLandlordMaxRate={selectedLandlordMaxRate}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignLandlord} disabled={attachingLandlord}>
            {attachingLandlord ? <CircularProgress size={24} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
